import { useCallback, useMemo, useRef, useState } from 'react'
import { useLatestRequestEffect } from '../../hooks/useLatestRequestEffect'
import { customFieldApiError, customFieldsApi, type CustomFieldCatalogResponse } from './api'
import type { CustomFieldEditorContext, CustomFieldEntityType } from './types'

const catalogRequests = new Map<string, Promise<CustomFieldCatalogResponse>>()

const catalogKey = (profileId: string, entityType: CustomFieldEntityType) =>
    `${profileId}:${entityType}`

const loadCatalog = (profileId: string, entityType: CustomFieldEntityType, force = false) => {
    const key = catalogKey(profileId, entityType)
    const pending = catalogRequests.get(key)
    if (pending && !force) return pending

    const request = customFieldsApi.catalog(profileId, entityType).finally(() => {
        // A forced refresh can replace an older in-flight request. Do not
        // let the older request remove the newer one from the dedupe map.
        if (catalogRequests.get(key) === request) catalogRequests.delete(key)
    })
    catalogRequests.set(key, request)
    return request
}

export const clearCustomFieldCatalogCache = () => {
    catalogRequests.clear()
}

export const useCustomFieldCatalog = (
    profileId: string | null | undefined,
    entityType: CustomFieldEntityType | null,
) => {
    const [catalog, setCatalog] = useState<CustomFieldCatalogResponse | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)
    const forceNextRequest = useRef(false)

    const refreshLatest = useLatestRequestEffect(
        () => {
            setError(null)
            if (!profileId || !entityType) return undefined
            setLoading(true)
            const force = forceNextRequest.current
            forceNextRequest.current = false
            return loadCatalog(profileId, entityType, force)
        },
        (nextCatalog) => {
            setCatalog(nextCatalog)
            setLoading(false)
        },
        [profileId, entityType],
        (requestError) => {
            setError(customFieldApiError(requestError))
            setLoading(false)
        },
        () => {
            setCatalog(null)
            setLoading(false)
        },
    )

    const refresh = useCallback(() => {
        // The next effect must bypass any older in-flight request. The browser
        // can revalidate the endpoint's versioned ETag; this module only
        // deduplicates simultaneous ordinary loads and never keeps a resolved
        // catalog under a versionless profile/entity key.
        forceNextRequest.current = true
        refreshLatest()
    }, [entityType, profileId, refreshLatest])

    // State updates are intentionally asynchronous, but consumers render with
    // the new profile/entity immediately. Never expose the previous scope's
    // catalog during that render-to-effect window.
    const scopedCatalog =
        catalog && catalog.profile_id === profileId && catalog.entity_type === entityType
            ? catalog
            : null

    const editorContext = useMemo<CustomFieldEditorContext | undefined>(
        () =>
            entityType
                ? { expectedEntityType: entityType, definitions: scopedCatalog?.definitions ?? [] }
                : undefined,
        [entityType, scopedCatalog],
    )

    return { catalog: scopedCatalog, editorContext, error, loading, refresh }
}
